/**
 * @file src/api/client.js
 */
import axios from 'axios';
import { storage } from '../utils/storage';
import { identityMismatch, tokenSubject, IdentityMismatchError } from '../utils/identityGuard.js';
import { isPublicPath } from '../routes/publicPaths';
import { isNeverDelivered, AWAY_RETRY_MS } from './neverDelivered';
import {
  COOKIE_AUTH_ENABLED,
  cookieAuthActive,
  CSRF_COOKIE_NAME,
  persistRefreshToken,
  readRefreshToken,
  canAttemptRefresh
} from '../utils/authTransport';
import { readGuestToken } from './guestToken.js';

// Single-flight refresh: only one refresh attempt runs at a time.
// All concurrent 401 responses queue behind the same promise.
let _refreshPromise = null;
let _csrfPromise = null;

const _readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : '';
};

const _ensureCsrfToken = async (axiosInstance) => {
  const current = _readCookie(CSRF_COOKIE_NAME);
  if (current) return current;

  if (!_csrfPromise) {
    _csrfPromise = axiosInstance.get('/csrf/token', {
      _skipCsrf: true,
      withCredentials: true
    })
      .then((response) => response.data?.data?.csrfToken || _readCookie(CSRF_COOKIE_NAME))
      .finally(() => {
        _csrfPromise = null;
      });
  }

  return _csrfPromise;
};

const _doRefresh = async (axiosInstance) => {
  const refreshToken = readRefreshToken();
  if (!canAttemptRefresh()) throw new Error('No refresh token');

  const response = await axiosInstance.post('/refresh', refreshToken ? { refreshToken } : {}, {
    _isRefreshCall: true // flag to skip the 401 interceptor loop
  });

  const { accessToken, refreshToken: newRefresh, transport } = response.data?.data || {};
  if (!accessToken) throw new Error('Refresh returned no access token');

  storage.set(storage.KEYS.ACCESS_TOKEN, accessToken);
  // Rotation still happens server-side in cookie mode — the new token comes
  // back as a Set-Cookie, so there is nothing for us to keep here.
  persistRefreshToken(newRefresh, transport);

  return accessToken;
};

/**
 * 🔑 The event the app listens for when a session dies.
 *
 * Exported so `useAuth` binds to the same string this file dispatches —
 * a mistyped literal on either side reintroduces the bug silently.
 */
export const SESSION_EXPIRED_EVENT = 'moksy:session-expired';



// One expiry, one teardown. The interceptor runs per RESPONSE, so a page
// that fires five parallel requests against a dead token used to clear
// storage five times and push five navigations — which is a large part of
// what the flashing was.
let _expiring = false;

const _redirectToLogin = () => {
  if (_expiring) return;
  _expiring = true;

  storage.clear();

  // ⚠️ Tell REACT, not just the browser.
  //
  // This used to clear storage and fake a popstate, and nothing else. The
  // auth context still held `user` in memory, so ProtectedRoute kept
  // rendering the page, its requests 401'd again, and the whole thing
  // looped — the app flickering between authenticated and not, while a
  // hard refresh (which re-reads the now-empty storage) revealed the
  // session had been gone the whole time.
  //
  // Clearing the in-memory user in the same tick means the redirect
  // happens once, from React's own router, on state React can see.
  // URL FIRST, then the event. `terminateSession` does its own navigation
  // guarded on `pathname.includes('/login')` — dispatching first means
  // that guard sees the old page, navigates to a bare `/login`, and the
  // `reason=session_expired` we set a line later is the second of two
  // navigations. Setting the URL first makes the guard a no-op and leaves
  // exactly one.
  // Carry the page the person was on. An expired session on /invite/<token>
  // or a shared report sent them to a bare login, and the link they had
  // clicked was gone; Login.jsx already honours `next`.
  const wasOn = `${window.location.pathname}${window.location.search}`;
  const next = wasOn && !wasOn.startsWith('/login') ? `&next=${encodeURIComponent(wasOn)}` : '';
  window.history.replaceState(null, '', `/login?reason=session_expired${next}`);
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  window.dispatchEvent(new PopStateEvent('popstate'));

  // Released on the next tick so a genuinely new session (the user logs
  // back in) can expire again later. Without this, the second expiry of a
  // long-lived tab would be swallowed.
  setTimeout(() => { _expiring = false; }, 0);
};

class GatewayClient {
  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1',
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: COOKIE_AUTH_ENABLED
    });

    this._initializeInterceptors();
  }

  _initializeInterceptors() {
    // 🛰️ REQUEST: Attach Identity Token (Standard 7)
    this.instance.interceptors.request.use((config) => {
      // The transport the server last reported wins over the build flag.
      if (cookieAuthActive()) {
        config.withCredentials = true;
      }

      const token = storage.get(storage.KEYS.ACCESS_TOKEN);
      const usable = storage.hasUsableAccessToken();

      // 🪪 The token must be the person this tab is (AuthOrchestrator stamps
      // USER_ID from the loaded profile). A mismatch means another login on
      // this machine has shadowed our token: drop both and refuse to send —
      // a request as somebody else must never leave the browser.
      const expected = storage.get(storage.KEYS.USER_ID);
      if (usable && token && expected && identityMismatch(token, expected)) {
        const sub = tokenSubject(token);
        // Detach THIS tab only (never the other person's remembered session),
        // and say so on the sign-in page: a tab that is nobody must not look
        // like a tab that is somebody.
        storage.detach();
        try { window.dispatchEvent(new CustomEvent('auth:identity-mismatch', { detail: { tokenSubject: sub, expectedUserId: expected } })); } catch (_) { /* no window */ }
        try {
          if (typeof window !== 'undefined' && !/^\/login/.test(window.location.pathname)) {
            const from = window.location.pathname + window.location.search;
            window.location.assign(`/login?reason=identity_changed&from=${encodeURIComponent(from)}`);
          }
        } catch (_) { /* no window */ }
        return Promise.reject(new IdentityMismatchError(sub, expected));
      }

      // Preserve explicitly provided Authorization headers (e.g., guest token flows).
      if (usable && token && !config.headers?.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // 🎫 Guest fallback.
      //
      // The gateway guards a set of routes with `authorizeAny` — a user token
      // OR a guest token, but not neither. This interceptor only ever attached
      // the ACCESS token, so a signed-out visitor sent no credential at all and
      // those routes 401'd. Three call sites independently wrote the same six
      // lines to work around it (guest downloads, the reviewer directory, the
      // category taxonomy) and every one of them was written only after the
      // bug had shipped.
      //
      // Safe to do globally, and measured rather than assumed: a guest token
      // sent to a user-only route behaves exactly like no token — /users/me
      // and /reviewer/me return 401 either way — so this can only satisfy the
      // routes written to accept it and cannot grant anything anywhere else.
      //
      // Skipped for the refresh call, which authenticates by refresh token.
      if (!config.headers?.Authorization && !config._isRefreshCall) {
        try {
          const guestToken = readGuestToken();
          if (guestToken) config.headers.Authorization = `Bearer ${guestToken}`;
        } catch { /* storage unavailable — carry on unauthenticated */ }
      }

      return (async () => {
        const method = String(config.method || 'get').toUpperCase();
        const unsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

        if (cookieAuthActive() && unsafe && !config._skipCsrf && !config.headers?.Authorization) {
          const csrfToken = await _ensureCsrfToken(this.instance);
          if (csrfToken) {
            config.headers = { ...config.headers, 'X-CSRF-Token': csrfToken };
          }
        }

        return config;
      })();
    });

    // 🔁 RESPONSE: Silent Token Refresh (Standard 13)
    this.instance.interceptors.response.use(
      (res) => res,
      async (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const isRefreshCall = error.config?._isRefreshCall;

        // Silent rejection for public assets — keeps console clean
        if (status === 404 && url?.includes('/assets/')) {
          return Promise.reject(error);
        }

        // Refused for maintenance: ask the app to re-read the state now, so the
        // banner or the maintenance page appears at once. The error still
        // reaches the caller, which keeps the person's text.
        if (status === 503 && /MAINTENANCE|MOKSY_OFFLINE/.test(String(error.response?.data?.code || ''))) {
          try { window.dispatchEvent(new Event('moksy:maintenance-check')); } catch (_) { /* no window */ }
        }

        // Never delivered (service was between processes) — send it once more
        // after a boot's worth of waiting. See isNeverDelivered.
        if (isNeverDelivered(status, error.response?.data?.code) && !error.config?._awayRetried) {
          await new Promise((resolve) => setTimeout(resolve, AWAY_RETRY_MS));
          return this.instance.request({ ...error.config, _awayRetried: true });
        }

        // 401 on an authenticated route: attempt a silent token refresh first.
        // Do not attempt for guests, public routes, or the refresh call itself.
        if (status === 401 && !isRefreshCall) {
          const hasRefreshToken = canAttemptRefresh();
          const path = window.location.pathname || '';
          // Was a second, shorter, hand-maintained copy of the public route
          // list. It knew about six paths out of twenty-six, so every public
          // page it had never heard of — the reviewer directory, pricing,
          // about, help, public profiles, shared reports — turned a routine
          // guest 401 into "Your session expired" and threw a first-time
          // visitor at the login form. One list now, in routes/publicPaths.js.
          const isPublicOrGuestPath = isPublicPath(path);
          // Public is a property of the PAGE, not of the person on it. A
          // signed-in owner reads their report on /projects/:id/preview —
          // a public path — and after the access token's 15 minutes every
          // Share click came back "invalid access token", because the
          // refresh was skipped for the path. Refresh whenever this tab is
          // somebody; the public-path rule only spares guests the redirect.
          const signedIn = Boolean(storage.get(storage.KEYS.ACCESS_TOKEN) || storage.get(storage.KEYS.USER_ID));

          if (hasRefreshToken && (!isPublicOrGuestPath || signedIn)) {
            try {
              // Single-flight: reuse existing refresh promise if one is in flight
              if (!_refreshPromise) {
                _refreshPromise = _doRefresh(this.instance).finally(() => {
                  _refreshPromise = null;
                });
              }
              const newAccessToken = await _refreshPromise;

              // Retry the original request with the new token
              const retryConfig = { ...error.config };
              retryConfig.headers = { ...retryConfig.headers, Authorization: `Bearer ${newAccessToken}` };
              return this.instance.request(retryConfig);
            } catch {
              // Refresh failed — session is truly expired
              _refreshPromise = null;
              if (!isPublicOrGuestPath) _redirectToLogin();
            }
          } else if (!isPublicOrGuestPath && storage.get(storage.KEYS.ACCESS_TOKEN)) {
            _redirectToLogin();
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

const clientInstance = new GatewayClient();

/**
 * Unwrap a gateway envelope safely.
 *
 * ⚠️ Replaces the `response.data?.data || response.data` idiom, which is
 * wrong for any endpoint whose payload can legitimately be null or empty.
 *
 * The gateway always answers `{ success, code, data, meta }`. When `data`
 * is null — "you have no reviewer profile yet", "no draft", "nothing
 * pending" — the old idiom fell through to `response.data` and handed the
 * caller THE ENVELOPE, which is a truthy object. Callers written as
 * `if (!profile) showApplyForm()` then took the wrong branch forever, and
 * read fields off a shape that never had them.
 *
 * That is exactly how the "Become a reviewer" page broke: an applicant
 * with no profile got `{success:true, data:null}` back, the page read it
 * as an existing profile, skipped the application form and rendered
 * "Status:" followed by nothing.
 *
 * Presence of the `data` KEY is what distinguishes an envelope from a
 * bare payload — not its truthiness. A response that is not an envelope
 * (a raw array, a service answering without the wrapper) passes through.
 */
export const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) {
    return body.data;
  }
  return body;
};

export default clientInstance.instance;
